import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pre-Consultation Discovery Questionnaire — data-driven config.
const FORM = [
  {
    title: "Client Profile & Primary Contact",
    fields: [
      { id: "fullName", label: "Primary Client Full Name", type: "text", required: true, core: "name", testid: "contact-name-input" },
      { id: "partner", label: "Co-Investor / Partner Name (if applicable)", type: "text", testid: "contact-partner-input" },
      { id: "email", label: "Email Address", type: "email", required: true, core: "email", testid: "contact-email-input" },
      { id: "phone", label: "Phone / WhatsApp Number (with country code)", type: "text", core: "phone", testid: "contact-phone-input" },
      { id: "residence", label: "Current Primary Country & City of Residence", type: "text", testid: "contact-residence-input" },
      { id: "citizenship", label: "Citizenship(s) / Passport(s) Held", type: "text", testid: "contact-citizenship-input" },
    ],
  },
  {
    title: "Professional Footprint & Global Mobility",
    fields: [
      { id: "profession", label: "Current Profession / Business Sector / Role", type: "text", testid: "contact-profession-input" },
      { id: "taxResidence", label: "Primary Country(ies) of Tax Residence", type: "text", testid: "contact-tax-residence-input" },
      {
        id: "flexibility", label: "Work & Lifestyle Flexibility", type: "pills", multi: true,
        options: ["Fully Remote / Location Independent", "Hybrid / Periodic Travel", "Locally Tied to Primary Residence"],
      },
      {
        id: "careerShift", label: "Anticipated Life / Career Shift (Next 3–5 Yrs)", type: "pills", multi: true,
        options: ["Selling / Exiting Business", "Relocating Internationally", "Retirement / Transitioning"],
      },
    ],
  },
  {
    title: "Family & Residency Objectives",
    fields: [
      {
        id: "family", label: "Family Structure & Dependents", type: "pills", multi: true,
        options: ["Single / Independent", "Spouse / Partner", "School-Aged Children", "University-Bound / Older"],
      },
      {
        id: "residency", label: "Residency & Citizenship Goals", type: "pills", multi: true,
        options: ["Seeking Golden Visa / RBI", "Plan B / Secondary Passport", "Tax Residency Relocation", "No Residency Needs (Pure ROI)"],
      },
    ],
  },
  {
    title: "Financial Parameters & Capital Allocation",
    fields: [
      {
        id: "capital", label: "Target Capital Deployment (Next 12–24 Months)", type: "pills",
        options: ["$15k – $50k", "$50k – $100k", "$100k or more"],
      },
      {
        id: "funding", label: "Funding & Financing Strategy", type: "pills", multi: true,
        options: ["100% Cash / Liquid Funds", "Local In-Country Mortgage Financing", "Equity Release / Cross-Border Financing"],
      },
    ],
  },
  {
    title: "Investment Strategy & Asset Preferences",
    fields: [
      {
        id: "driver", label: "Primary Investment Driver (Select Top 2)", type: "pills", multi: true, max: 2,
        options: ["High Net Rental Yield (Cash Flow)", "Long-Term Capital Growth / Appreciation", "Capital Preservation & Currency Hedge", "Lifestyle, Vacation & Personal Family Use"],
      },
      {
        id: "management", label: "Management Level & Asset Class", type: "pills", multi: true,
        options: ["100% Turnkey Fully Managed (Hands-off)", "Short-Term / Vacation Rental Strategy", "Long-Term Residential Lease", "Commercial / Mixed-Use Development"],
      },
      { id: "targetRegions", label: "Target Countries or Regions under Consideration", type: "text", testid: "contact-target-regions-input" },
      { id: "barrier", label: "Biggest Concern or Perceived Barrier in Overseas Investing", type: "text", testid: "contact-barrier-input" },
      { id: "focus", label: "Specific Questions or Key Focus Areas for Our Strategy Session", type: "textarea", core: "message", testid: "contact-message-input" },
    ],
  },
];

const ALL_FIELDS = FORM.flatMap((s) => s.fields);
const initialState = () =>
  ALL_FIELDS.reduce((acc, f) => {
    acc[f.id] = f.type === "pills" ? [] : "";
    return acc;
  }, {});

const inputCls =
  "w-full bg-transparent border border-gl-gold/25 px-4 py-3 text-gl-text placeholder:text-gl-text2/40 outline-none focus:border-gl-gold transition-colors duration-300";

export default function ContactForm() {
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const setText = (id) => (e) => setValues((v) => ({ ...v, [id]: e.target.value }));

  const togglePill = (field, opt) => {
    setValues((v) => {
      if (!field.multi) return { ...v, [field.id]: v[field.id] === opt ? "" : opt };
      const cur = v[field.id] || [];
      if (cur.includes(opt)) return { ...v, [field.id]: cur.filter((o) => o !== opt) };
      if (field.max && cur.length >= field.max) {
        toast.info(`Please select up to ${field.max} for "${field.label}".`);
        return v;
      }
      return { ...v, [field.id]: [...cur, opt] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!values.fullName.trim() || !values.email.trim()) {
      toast.error("Please provide your full name and email address.");
      return;
    }
    // Build core + structured payload
    const payload = { name: values.fullName.trim(), email: values.email.trim() };
    ALL_FIELDS.forEach((f) => {
      if (f.core && f.core !== "name" && f.core !== "email") {
        const val = Array.isArray(values[f.id]) ? values[f.id].join(", ") : values[f.id];
        payload[f.core] = val || "";
      }
    });
    payload.interest = Array.isArray(values.driver) ? values.driver.join(", ") : "";
    payload.sections = FORM.map((sec) => ({
      title: sec.title,
      fields: sec.fields
        .map((f) => ({
          label: f.label,
          value: Array.isArray(values[f.id]) ? values[f.id].join(", ") : (values[f.id] || "").trim(),
        }))
        .filter((f) => f.value),
    })).filter((sec) => sec.fields.length);

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, payload);
      toast.success("Thank you — your questionnaire has been received. We'll be in touch shortly.");
      setValues(initialState());
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-12">
      <p className="text-gl-text2 font-light leading-relaxed -mt-2">
        Confidential assessment · Est. 2–5 mins. Your answers let us tailor tax regimes,
        residency pathways, financing structures, and market opportunities to your profile.
      </p>

      {FORM.map((section, si) => (
        <div key={section.title} className="space-y-6">
          <div className="flex items-baseline gap-4">
            <span className="font-serif-display text-xl text-gl-gold">
              {String(si + 1).padStart(2, "0")}
            </span>
            <h3 className="font-serif-display text-2xl text-gl-text">{section.title}</h3>
          </div>

          <div className="space-y-6">
            {section.fields.map((f) => (
              <div key={f.id}>
                <label
                  htmlFor={f.type === "pills" ? undefined : f.id}
                  className="block text-xs uppercase tracking-[0.18em] text-gl-text2 mb-3"
                >
                  {f.label}
                  {f.required && <span className="text-gl-gold"> *</span>}
                </label>

                {f.type === "textarea" ? (
                  <textarea
                    id={f.id}
                    data-testid={f.testid}
                    value={values[f.id]}
                    onChange={setText(f.id)}
                    rows={4}
                    placeholder="Share anything you'd like us to prepare for…"
                    className={`${inputCls} resize-none`}
                  />
                ) : f.type === "pills" ? (
                  <div className="flex flex-wrap gap-2.5">
                    {f.options.map((opt) => {
                      const active = f.multi
                        ? (values[f.id] || []).includes(opt)
                        : values[f.id] === opt;
                      return (
                        <button
                          type="button"
                          key={opt}
                          aria-pressed={active}
                          data-testid={`pill-${f.id}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          onClick={() => togglePill(f, opt)}
                          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border transition-all duration-300 ${
                            active
                              ? "bg-gl-gold text-gl-bg border-gl-gold"
                              : "bg-transparent text-gl-text2 border-gl-gold/25 hover:border-gl-gold/60 hover:text-gl-text"
                          }`}
                        >
                          {active && <Check size={14} />}
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <input
                    id={f.id}
                    data-testid={f.testid}
                    type={f.type === "email" ? "email" : "text"}
                    value={values[f.id]}
                    onChange={setText(f.id)}
                    className={inputCls}
                  />
                )}
              </div>
            ))}
          </div>

          {si < FORM.length - 1 && <div className="h-px bg-gl-gold/12 mt-2" />}
        </div>
      ))}

      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={loading}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 bg-gl-gold text-gl-bg font-medium tracking-wide hover:bg-gl-goldHover transition-colors duration-300 disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Sending…
          </>
        ) : (
          <>
            <Send size={18} /> Submit Questionnaire
          </>
        )}
      </button>
    </form>
  );
}
